import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { HirePlansView, SaveHirePlansRequest } from '@nest-recruit/shared';
import { apiGet, apiPut, type ApiError } from '../../../lib/api-client';

/** ADR 0083 — HIRE-01. The server's realtime hint names `hire-plans` (and `hires`, whose Confirm joining waits on these). */
export const HIRE_PLANS_KEY = ['hire-plans'] as const;

export function hirePlansKey(applicationId: string): readonly unknown[] {
  return [...HIRE_PLANS_KEY, applicationId];
}

export function useHirePlans(applicationId: string): UseQueryResult<HirePlansView, ApiError> {
  return useQuery({
    queryKey: hirePlansKey(applicationId),
    queryFn: () => apiGet<HirePlansView>(`/applications/${applicationId}/plans`),
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

/** The form keeps what was typed on screen while it saves; the answer replaces the cached view. */
export function useSaveHirePlans(applicationId: string): UseMutationResult<HirePlansView, ApiError, SaveHirePlansRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiPut<HirePlansView>(`/applications/${applicationId}/plans`, body),
    onSuccess: (view) => {
      queryClient.setQueryData(hirePlansKey(applicationId), view);
      void queryClient.invalidateQueries({ queryKey: ['hires'] });
    },
  });
}
